import { Modality } from "@google/genai";
import { Timestamp } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, X } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import eventBus from "../../utils/eventBus";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import dreamWorldProvider from "../../DreamWorldProvider";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import {
  EditingTurn,
  InteractiveEditingData,
  LibraryItem,
} from "../../types/library";
import uploadImageToFirebase from "../../utils/uploadImageToFirebase";
import CreationReadyModal from "../CreationReadyModal";
import EffectDescriptionPanel from "../create/EffectDescriptionPanel";
import EffectCard from "../EffectCard";
import { Effect, Style } from "../../types/effects";
import {
  addPhotoEffects,
  selectHasMorePhotoEffects,
  selectPhotoEffects,
  selectPhotoEffectsLastVisible,
  setHasMorePhotoEffects,
  setPhotoEffects,
  setPhotoEffectsLastVisible,
  selectShowPhotoEffectsModal,
  setShowPhotoEffectsModal,
} from "../../store/effectsSlice";
import InfiniteScroll from "react-infinite-scroll-component";
import Section from "../ui/Section";

const PhotoEffectsComponent: React.FC = () => {
  const [selectedEffect, setSelectedEffect] = useState<Effect | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [displayCount, setDisplayCount] = useState(10);
  const [isCreationReadyModalOpen, setIsCreationReadyModalOpen] =
    useState(false);
  const [newCreationId, setNewCreationId] = useState<string | null>(null);
  const userGlobalData = useSelector(selectUserGlobalData);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const photoEffects = useSelector(selectPhotoEffects);
  const lastVisible = useSelector(selectPhotoEffectsLastVisible);
  const hasMore = useSelector(selectHasMorePhotoEffects);
  const showAll = useSelector(selectShowPhotoEffectsModal);

  useEffect(() => {
    const fetchEffects = async () => {
      if (photoEffects.length === 0 && userGlobalData?.user?.uid) {
        const { effects, lastVisible: newLastVisible } =
          await dreamWorldProvider.getEffects(
            "photo",
            userGlobalData.user.uid
          );
        dispatch(setPhotoEffects(effects));
        dispatch(setPhotoEffectsLastVisible(newLastVisible));
        if (effects.length < 20) {
          dispatch(setHasMorePhotoEffects(false));
        }
      }
    };

    fetchEffects();
  }, [dispatch, photoEffects.length, userGlobalData]);

  // Calculate display count based on screen size for desktop grid
  useEffect(() => {
    if (showAll) {
      eventBus.emit("fullscreen:enter");
    } else {
      eventBus.emit("fullscreen:exit");
    }
  }, [showAll]);
  useEffect(() => {
    const updateDisplayCount = () => {
      const width = window.innerWidth;
      let columnsPerRow = 5; // Default for sm screens

      if (width >= 1024) {
        // lg breakpoint
        columnsPerRow = 8;
      } else if (width >= 768) {
        // md breakpoint
        columnsPerRow = 7;
      } else if (width >= 640) {
        // sm breakpoint
        columnsPerRow = 6;
      }

      // For mobile, keep the original 10 items
      // For desktop, show 2 full rows
      const newDisplayCount = width < 640 ? 10 : columnsPerRow * 2;
      setDisplayCount(newDisplayCount);
    };

    updateDisplayCount();
    window.addEventListener("resize", updateDisplayCount);
    return () => window.removeEventListener("resize", updateDisplayCount);
  }, []);

  const handleEffectClick = (effect: Effect) => {
    setSelectedEffect(effect);
  };

  const fetchMoreEffects = async () => {
    if (hasMore && userGlobalData?.user?.uid) {
      const { effects, lastVisible: newLastVisible } =
        await dreamWorldProvider.getEffects(
          "photo",
          userGlobalData.user.uid,
          lastVisible
        );
      dispatch(addPhotoEffects(effects));
      dispatch(setPhotoEffectsLastVisible(newLastVisible));
      if (effects.length < 20) {
        dispatch(setHasMorePhotoEffects(false));
      }
    }
  };

  const handleClosePanel = () => {
    setSelectedEffect(null);
  };
  const saveTurnToLibraryAndGetId = async (
    turn: EditingTurn
  ): Promise<string> => {
    if (!userGlobalData) {
      throw new Error("User data is not available.");
    }

    const newLibraryItem: LibraryItem = {
      id: uuidv4(),
      ownerId: userGlobalData.user.uid,
      type: "Image",
      fullUrl: turn.generatedImageUrl,
      thumbnailUrl: turn.generatedImageUrl,
      originalImageUrl: turn.generatedImageUrl[0],
      prompt: turn.request,
      isFavorite: false,
      views: 0,
      likes: 0,
      tags: [],
      size: "",
      createdAt: Timestamp.now(),
      isPublished: false,
      title: turn.request,
      effectId: selectedEffect?.id,
    };

    const libraryItemId =
      await dreamWorldProvider.saveLibraryItem(newLibraryItem);
    return libraryItemId;
  };

  const handleStartEditing = async (
    effect: Effect,
    images: File[],
    userInput?: string,
    style?: Style
  ) => {
    if (!userGlobalData || images.length === 0) return;

    setIsEditing(true);

    try {
      const originalImageUrls = await Promise.all(
        images.map((image) => uploadImageToFirebase(image, "originalImages"))
      );

      const ai = dreamWorldProvider.getImageAI();

      const imageParts = await Promise.all(
        images.map(async (image) => {
          const fileAsBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = () =>
              resolve((reader.result as string).split(",")[1]);
            reader.onerror = (error) => reject(error);
          });
          return {
            inlineData: {
              mimeType: image.type,
              data: fileAsBase64,
            },
          };
        })
      );

      let promptText = userInput
        ? `${effect.prompt} User input: ${userInput}`
        : effect.prompt;

      if (style) {
        promptText = `${promptText} Style: ${style.name}`;
      }

      const contents = [{ text: promptText }, ...imageParts];

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-image-preview",
        contents: contents,
        config: {
          responseModalities: [Modality.TEXT, Modality.IMAGE],
        },
      });

      if (
        response.candidates &&
        response.candidates.length > 0 &&
        response.candidates[0].content &&
        response.candidates[0].content.parts
      ) {
        for (const part of response.candidates[0].content.parts) {
          if (part.inlineData && part.inlineData.data) {
            const imageData = part.inlineData.data;
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            const file = new File([blob], "edited_image.png", {
              type: "image/png",
            });
            const generatedImageUrl = await uploadImageToFirebase(
              file,
              "postsImages"
            );

            const docId = uuidv4();
            var newEditingData: InteractiveEditingData = {
              id: docId,
              ownerId: userGlobalData.user.uid,
              title: effect.title,
              originalImageUrls: originalImageUrls,
              createdAt: Timestamp.now(),
              turnsCount: 1,
              effectId: effect.id,
              editings: [
                {
                  id: uuidv4(),
                  imageUrl: originalImageUrls,
                  request: promptText,
                  generatedImageUrl,
                  date: Timestamp.now(),
                  isSavedToLib: false,
                  isPublished: false,
                },
              ],
            };
            const libraryId = await saveTurnToLibraryAndGetId(
              newEditingData.editings[0]
            );
            newEditingData.editings[0].libraryItemId = libraryId;

            await dreamWorldProvider.addInteractiveEditingData(newEditingData);
            setNewCreationId(docId);
            setIsCreationReadyModalOpen(true);
            handleClosePanel();
          }
        }
      }
    } catch (error) {
      console.error("Error starting interactive editing:", error);
    } finally {
      setIsEditing(false);
      setSelectedEffect(null);
    }
  };

  const displayedEffects = photoEffects.slice(0, displayCount);

  return (
    <Section
      title="Photo Effects"
      onSeeAll={() => dispatch(setShowPhotoEffectsModal(true))}
    >
      <div className=" mx-auto px-0 ">
        {/* Mobile: Horizontal scroll */}
        <div className="sm:hidden">
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-1.5 min-w-max">
              {displayedEffects.map((effect, index) => (
                <div
                  key={effect.id}
                  style={{
                    marginLeft: index === 0 ? "8px" : undefined,
                    marginRight:
                      index === displayedEffects.length - 1 ? "8px" : undefined,
                  }}
                >
                  <EffectCard
                    effect={effect}
                    onClick={() => handleEffectClick(effect)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Wrapped grid */}
        <div className="hidden sm:grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8 gap-1 px-2">
          {displayedEffects.map((effect) => (
            <EffectCard
              key={effect.id}
              effect={effect}
              onClick={() => handleEffectClick(effect)}
            />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showAll && (
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 bg-gray-900/90 backdrop-blur-xl z-50 overflow-y-auto"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-3xl font-bold text-white">
                  All Photo Effects
                </h2>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  onClick={() => dispatch(setShowPhotoEffectsModal(false))}
                  className="p-2 rounded-full hover:bg-white/10"
                >
                  <X className="text-white" />
                </motion.button>
              </div>
              <InfiniteScroll
                dataLength={photoEffects.length}
                next={fetchMoreEffects}
                hasMore={hasMore}
                loader={<h4>Loading...</h4>}
                endMessage={
                  <p style={{ textAlign: "center" }}>
                    <b>Yay! You have seen it all</b>
                  </p>
                }
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6"
              >
                {photoEffects.map((effect) => (
                  <motion.div
                    key={effect.id}
                    className="bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-purple-500/30 transition-all cursor-pointer group"
                    onClick={() => {
                      handleEffectClick(effect);
                    }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src={effect.image}
                      alt={effect.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="text-white font-bold truncate">
                        {effect.title}
                      </h3>
                      <p className="text-gray-400 text-sm mt-1 h-10 overflow-hidden">
                        {effect.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </InfiniteScroll>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <EffectDescriptionPanel
        effect={selectedEffect}
        onClose={handleClosePanel}
        onStartEditing={handleStartEditing}
        preventExitFullscreen={showAll}
      />
      <CreationReadyModal
        isOpen={isCreationReadyModalOpen}
        onClose={() => setIsCreationReadyModalOpen(false)}
        onNavigate={() => {
          if (newCreationId) {
            navigate(`/interactive-editing/${newCreationId}`);
          }
        }}
      />
    </Section>
  );
};

export default PhotoEffectsComponent;
